/**
 * What an upload row says when it fails, and whether trying again can help
 * (`architecture/0031`).
 *
 * Shared by the three upload queues — project documents, journal photographs
 * and crew certificates — so a person reads the same words for the same fault
 * wherever they dropped the file.
 */

/**
 * The browser's own words for a dropped connection are "Failed to fetch",
 * "Load failed" or "NetworkError when attempting to fetch resource.",
 * depending on the browser. None of them says what happened or what to do.
 */
export const CONNECTION_DROPPED = "The connection dropped. Try again.";

/** Storage answered the PUT, but not with success: most often an expired link. */
export const UPLOAD_DIDNT_GO_THROUGH = "The upload didn’t go through. Try again.";

/**
 * A refusal the same file will always get. The check before sending found it,
 * or the server answered 400 or 413. Retrying cannot help, so the row offers
 * Dismiss and no Retry.
 */
export class UploadRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UploadRefused";
  }
}

/** Whether a status from an upload route is a refusal of the file itself. */
export function isRefusal(status: number): boolean {
  return status === 400 || status === 413;
}

/** The row's words for a failure, and whether it is a refusal. */
export function uploadFailure(error: unknown): { error: string; refused: boolean } {
  if (error instanceof UploadRefused) return { error: error.message, refused: true };
  // `fetch` rejects with a TypeError when the connection drops. A TypeError
  // from a bug in this code would read the same way, which is the lesser harm:
  // it still offers a Retry, and the console keeps the real error.
  if (error instanceof TypeError) {
    console.warn("Upload failed:", error);
    return { error: CONNECTION_DROPPED, refused: false };
  }
  if (error instanceof Error && error.message) return { error: error.message, refused: false };
  return { error: UPLOAD_DIDNT_GO_THROUGH, refused: false };
}
